import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Palette, Image, User, Save, Eye, Car, Mail, Send, AlertCircle, Hash, Lock, Upload, RefreshCw, CheckCircle2, XCircle, Wifi, WifiOff, PenTool, Users, Trash2, KeyRound, Monitor, Tablet, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const ACCENT_PRESETS = [
  { name: "Rosso", hex: "#DC2626" },
  { name: "Blu", hex: "#2563EB" },
  { name: "Verde", hex: "#16A34A" },
  { name: "Viola", hex: "#7C3AED" },
  { name: "Arancione", hex: "#EA580C" },
  { name: "Teal", hex: "#0D9488" },
  { name: "Rosa", hex: "#DB2777" },
  { name: "Slate", hex: "#475569" },
];

export default function SettingsPage() {
  const { axiosAuth, user } = useAuth();
  const { settings, updateSettings } = useSettings();
  const accent = settings?.primary_color || "#DC2626";
  const [appForm, setAppForm] = useState({ app_name: "", app_tagline: "", app_icon_url: "", login_image_url: "", login_quote: "", primary_color: "#DC2626", color_theme: "classic" });
  const [profileForm, setProfileForm] = useState({ name: "", avatar_url: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [emailForm, setEmailForm] = useState({ provider: "", config: {}, templates: {} });
  const [contractCounter, setContractCounter] = useState({ year: new Date().getFullYear(), sequence: 0 });
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [previewLogin, setPreviewLogin] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [signatureScale, setSignatureScale] = useState(1.0);
  const [saveTabletSignature, setSaveTabletSignature] = useState(false);
  const [signaturePositions, setSignaturePositions] = useState([
    { x: 380, y: 390, label: "Firma 1 - Consegna" },
    { x: 380, y: 428, label: "Firma 2 - Clausole" },
    { x: 380, y: 535, label: "Firma 3 - Responsabile" },
  ]);
  const [contractPreview, setContractPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dragging, setDragging] = useState(null);
  const previewRef = useRef(null);
  const [pdfDims, setPdfDims] = useState({ w: 595, h: 842 });
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updaterOnline, setUpdaterOnline] = useState(null);
  const [kioskPin, setKioskPin] = useState("");
  const [users, setUsers] = useState([]);
  const [resetPwUserId, setResetPwUserId] = useState(null);
  const [resetPwValue, setResetPwValue] = useState("");

  useEffect(() => {
    setAppForm({ app_name: settings.app_name || "", app_tagline: settings.app_tagline || "", app_icon_url: settings.app_icon_url || "", login_image_url: settings.login_image_url || "", login_quote: settings.login_quote || "", primary_color: settings.original_primary_color || settings.primary_color || "#DC2626", color_theme: settings.color_theme || "classic" });
    setSignatureScale(settings.signature_scale || 1.0);
    if (settings.signature_positions) setSignaturePositions(settings.signature_positions);
    setSaveTabletSignature(settings.save_tablet_signature || false);
  }, [settings]);

  useEffect(() => {
    const loadFull = async () => {
      try {
        const api = axiosAuth();
        const res = await api.get("/settings/full");
        const s = res.data || {};
        setEmailForm({ provider: s.email_provider || "", config: s.email_config || {}, templates: s.email_templates || {} });
        setKioskPin(s.kiosk_pin || "");
      } catch { /* ignore */ }
    };
    loadFull();
  }, [axiosAuth]);

  const fetchProfile = useCallback(async () => {
    try { const api = axiosAuth(); const res = await api.get("/auth/profile"); setProfileForm({ name: res.data.name || "", avatar_url: res.data.avatar_url || "" }); }
    catch { setProfileForm({ name: user?.name || "", avatar_url: "" }); }
  }, [axiosAuth, user]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const fetchUsers = useCallback(async () => {
    try { const api = axiosAuth(); const res = await api.get("/auth/users"); setUsers(res.data); }
    catch { /* ignore */ }
  }, [axiosAuth]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchCounter = useCallback(async () => {
    try { const api = axiosAuth(); const res = await api.get("/settings/contract-counter"); setContractCounter(res.data); }
    catch { /* ignore */ }
  }, [axiosAuth]);
  useEffect(() => { fetchCounter(); }, [fetchCounter]);

  const checkUpdateStatus = useCallback(async () => {
    try {
      const api = axiosAuth();
      const res = await api.get("/settings/update-status");
      setUpdateStatus(res.data);
      setUpdaterOnline(res.data.updater_online);
      return res.data;
    } catch {
      setUpdaterOnline(false);
      return null;
    }
  }, [axiosAuth]);

  useEffect(() => { checkUpdateStatus(); }, [checkUpdateStatus]);

  const triggerUpdate = async () => {
    if (!window.confirm("Vuoi aggiornare l'app? Il sito sara' temporaneamente non disponibile durante il rebuild.")) return;
    setUpdating(true);
    try {
      const api = axiosAuth();
      await api.post("/settings/trigger-update");
      toast.success("Aggiornamento avviato! Attendi qualche minuto...");
      // Poll status every 5 seconds
      const poll = setInterval(async () => {
        const s = await checkUpdateStatus();
        if (s && s.status !== "in_progress") {
          clearInterval(poll);
          setUpdating(false);
          if (s.status === "success") {
            toast.success("Aggiornamento completato! La pagina si ricarichera'.");
            setTimeout(() => window.location.reload(), 3000);
          } else if (s.status === "error") {
            toast.error("Errore durante l'aggiornamento: " + (s.message || ""));
          }
        }
      }, 5000);
      // Safety timeout: stop polling after 60 minutes
      setTimeout(() => { clearInterval(poll); setUpdating(false); }, 3600000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore avvio aggiornamento");
      setUpdating(false);
    }
  };

  const uploadImage = async (file, target) => {
    setUploading(target);
    try {
      const api = axiosAuth();
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data.url;
      if (target === "app_icon") {
        const newForm = { ...appForm, app_icon_url: url };
        setAppForm(newForm);
        // Auto-save
        const saveRes = await api.put("/settings", newForm);
        updateSettings(saveRes.data);
      } else if (target === "login_image") {
        const newForm = { ...appForm, login_image_url: url };
        setAppForm(newForm);
        // Auto-save
        const saveRes = await api.put("/settings", newForm);
        updateSettings(saveRes.data);
      } else if (target === "avatar") {
        setProfileForm(prev => ({ ...prev, avatar_url: url }));
        // Auto-save profile
        await api.put("/auth/profile", { ...profileForm, avatar_url: url });
      }
      toast.success("Immagine salvata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(null); }
  };

  const saveContractCounter = async () => {
    setSaving(true);
    try {
      const api = axiosAuth();
      await api.put("/settings/contract-counter", contractCounter);
      toast.success("Numerazione aggiornata! Il prossimo contratto sarà N." + (contractCounter.sequence + 1) + "/" + contractCounter.year);
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const saveAppSettings = async () => {
    setSaving(true);
    try { const api = axiosAuth(); const res = await api.put("/settings", appForm); updateSettings(res.data); toast.success("Salvato!"); }
    catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try { const api = axiosAuth(); await api.put("/auth/profile", profileForm); toast.success("Profilo aggiornato!"); }
    catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error("Le password non coincidono"); return; }
    if (passwordForm.new_password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setSaving(true);
    try {
      const api = axiosAuth();
      await api.put("/auth/change-password", { current_password: passwordForm.current_password, new_password: passwordForm.new_password });
      toast.success("Password aggiornata!");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      const api = axiosAuth();
      const res = await api.put("/settings", { email_provider: emailForm.provider, email_config: emailForm.config, email_templates: emailForm.templates });
      updateSettings(res.data);
      toast.success("Impostazioni email salvate!");
    } catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const saveSignatureScale = async () => {
    setSaving(true);
    try {
      const api = axiosAuth();
      const res = await api.put("/settings", { signature_scale: signatureScale, signature_positions: signaturePositions, save_tablet_signature: saveTabletSignature });
      updateSettings(res.data);
      toast.success("Impostazioni firma salvate!");
    } catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const loadContractPreview = async () => {
    setPreviewLoading(true);
    try {
      const api = axiosAuth();
      const res = await api.get("/signatures/contract-preview/image");
      setContractPreview(res.data.image);
      setPdfDims({ w: res.data.pdf_width, h: res.data.pdf_height });
    } catch { toast.error("Errore caricamento anteprima"); }
    finally { setPreviewLoading(false); }
  };

  const handleMouseDown = (idx, e) => {
    e.preventDefault();
    setDragging(idx);
  };

  const handleMouseMove = useCallback((e) => {
    if (dragging === null || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const scaleX = pdfDims.w / rect.width;
    const scaleY = pdfDims.h / rect.height;
    const x = Math.max(0, Math.min(pdfDims.w, (e.clientX - rect.left) * scaleX));
    const y = Math.max(0, Math.min(pdfDims.h, (e.clientY - rect.top) * scaleY));
    setSignaturePositions(prev => prev.map((p, i) => i === dragging ? { ...p, x: Math.round(x), y: Math.round(y) } : p));
  }, [dragging, pdfDims]);

  const handleMouseUp = useCallback(() => { setDragging(null); }, []);

  useEffect(() => {
    if (dragging !== null) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => { window.removeEventListener("mousemove", handleMouseMove); window.removeEventListener("mouseup", handleMouseUp); };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error("Inserisci email destinatario"); return; }
    setTestingEmail(true);
    try { const api = axiosAuth(); await api.post("/email/test", { to_email: testEmail }); toast.success("Email di test inviata!"); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore invio"); }
    finally { setTestingEmail(false); }
  };

  const updateConfig = (key, val) => setEmailForm({ ...emailForm, config: { ...emailForm.config, [key]: val } });
  const updateTemplate = (tplKey, field, val) => setEmailForm({ ...emailForm, templates: { ...emailForm.templates, [tplKey]: { ...emailForm.templates[tplKey], [field]: val } } });

  const templateLabels = { booking_confirmation: "Conferma Prenotazione", rental_reminder: "Promemoria Noleggio", return_reminder: "Promemoria Riconsegna" };
  const placeholders = "{nome_cliente}, {veicolo}, {targa}, {data_inizio}, {ora_inizio}, {data_fine}, {ora_fine}, {totale}, {numero_contratto}, {nome_app}";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-slate-100">Impostazioni</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">Personalizza app, login, profilo e email</p>
      </div>

      <Tabs defaultValue="app">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-lg flex-wrap" data-testid="settings-tabs">
          <TabsTrigger value="app" className="rounded-md text-sm"><Palette className="w-3.5 h-3.5 mr-1" /> App</TabsTrigger>
          <TabsTrigger value="login" className="rounded-md text-sm"><Image className="w-3.5 h-3.5 mr-1" /> Login</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-md text-sm"><User className="w-3.5 h-3.5 mr-1" /> Profilo</TabsTrigger>
          <TabsTrigger value="contracts" className="rounded-md text-sm"><Hash className="w-3.5 h-3.5 mr-1" /> Contratti</TabsTrigger>
          <TabsTrigger value="signature" className="rounded-md text-sm"><PenTool className="w-3.5 h-3.5 mr-1" /> Firma</TabsTrigger>
          <TabsTrigger value="email" className="rounded-md text-sm"><Mail className="w-3.5 h-3.5 mr-1" /> Email</TabsTrigger>
          <TabsTrigger value="system" className="rounded-md text-sm"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Sistema</TabsTrigger>
          <TabsTrigger value="users" className="rounded-md text-sm"><Users className="w-3.5 h-3.5 mr-1" /> Utenti</TabsTrigger>
        </TabsList>

        {/* App */}
        <TabsContent value="app" className="mt-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader><CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Identita App</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Nome App</Label><Input data-testid="settings-app-name" value={appForm.app_name} onChange={(e) => setAppForm({ ...appForm, app_name: e.target.value })} className="h-10 rounded-lg" /></div>
              <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Sottotitolo</Label><Input data-testid="settings-app-tagline" value={appForm.app_tagline} onChange={(e) => setAppForm({ ...appForm, app_tagline: e.target.value })} className="h-10 rounded-lg" /></div>

              {/* Icon Upload */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200">Icona/Logo</Label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden shrink-0 border-2 border-dashed border-slate-300 dark:border-slate-600" style={{ backgroundColor: appForm.app_icon_url ? 'transparent' : accent }}>
                    {appForm.app_icon_url ? <img src={appForm.app_icon_url} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} /> : <Car className="w-5 h-5 text-white" />}
                  </div>
                  <label className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm text-slate-700 dark:text-slate-300" data-testid="upload-icon-btn">
                    <Upload className="w-4 h-4" />
                    {uploading === "app_icon" ? "Caricamento..." : "Carica Immagine"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "app_icon")} />
                  </label>
                </div>
              </div>

              {/* Tema Gestionale */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200">Tema Gestionale</Label>
                <div className="grid grid-cols-2 gap-3" data-testid="theme-preset-picker">
                  <button
                    onClick={() => setAppForm({ ...appForm, color_theme: "classic" })}
                    className={`rounded-xl border-2 p-3 text-left transition-all ${appForm.color_theme !== "gold" ? "border-slate-900 dark:border-slate-100" : "border-slate-200 dark:border-slate-700 opacity-70 hover:opacity-100"}`}
                    data-testid="theme-preset-classic"
                  >
                    <div className="flex gap-1.5 mb-2">
                      <span className="w-6 h-6 rounded-md bg-slate-900" />
                      <span className="w-6 h-6 rounded-md" style={{ backgroundColor: appForm.primary_color }} />
                      <span className="w-6 h-6 rounded-md bg-slate-100 border border-slate-200" />
                    </div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Classico</p>
                    <p className="text-[11px] text-slate-400">Tema attuale con colore pulsanti personalizzabile</p>
                  </button>
                  <button
                    onClick={() => setAppForm({ ...appForm, color_theme: "gold" })}
                    className={`rounded-xl border-2 p-3 text-left transition-all ${appForm.color_theme === "gold" ? "border-slate-900 dark:border-slate-100" : "border-slate-200 dark:border-slate-700 opacity-70 hover:opacity-100"}`}
                    data-testid="theme-preset-gold"
                  >
                    <div className="flex gap-1.5 mb-2">
                      <span className="w-6 h-6 rounded-md" style={{ backgroundColor: "#0b0b0c" }} />
                      <span className="w-6 h-6 rounded-md" style={{ backgroundColor: "#D9A441" }} />
                      <span className="w-6 h-6 rounded-md" style={{ backgroundColor: "#151517" }} />
                    </div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">RG Gold</p>
                    <p className="text-[11px] text-slate-400">Nero e oro, i colori del sito vetrina</p>
                  </button>
                </div>
              </div>

              {/* Accent Color Picker */}
              <div className={`space-y-2 ${appForm.color_theme === "gold" ? "opacity-50 pointer-events-none" : ""}`}>
                <Label className="text-slate-700 dark:text-slate-200">Colore Pulsanti {appForm.color_theme === "gold" && "(gestito dal tema RG Gold)"}</Label>
                <div className="flex flex-wrap gap-2" data-testid="accent-color-picker">
                  {ACCENT_PRESETS.map(c => (
                    <button
                      key={c.hex}
                      onClick={() => setAppForm({ ...appForm, primary_color: c.hex })}
                      className={`w-9 h-9 rounded-lg transition-all ${appForm.primary_color === c.hex ? "ring-2 ring-offset-2 ring-slate-900 dark:ring-slate-100 dark:ring-offset-slate-900 scale-110" : "hover:scale-105"}`}
                      style={{ backgroundColor: c.hex }}
                      title={c.name}
                      data-testid={`accent-${c.name.toLowerCase()}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="color" value={appForm.primary_color} onChange={(e) => setAppForm({ ...appForm, primary_color: e.target.value })} className="w-10 h-8 p-0.5 rounded cursor-pointer" data-testid="accent-custom-color" />
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{appForm.primary_color}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden shrink-0" style={{ backgroundColor: appForm.primary_color }}>
                  {appForm.app_icon_url ? <img src={appForm.app_icon_url} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} /> : <Car className="w-5 h-5 text-white" />}
                </div>
                <div><p className="font-outfit font-bold text-slate-900 dark:text-slate-100 text-sm">{appForm.app_name || "Noleggio Veloce"}</p><p className="text-[11px] text-slate-400">{appForm.app_tagline}</p></div>
              </div>
              <Button onClick={saveAppSettings} disabled={saving} data-testid="save-app-settings-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg"><Save className="w-4 h-4 mr-2" />{saving ? "..." : "Salva"}</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Login */}
        <TabsContent value="login" className="mt-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader><CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Pagina Login</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200">Immagine Login</Label>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-14 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700 shrink-0">
                    {appForm.login_image_url && <img src={appForm.login_image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <label className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm text-slate-700 dark:text-slate-300" data-testid="upload-login-image-btn">
                    <Upload className="w-4 h-4" />
                    {uploading === "login_image" ? "Caricamento..." : "Carica Immagine"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "login_image")} />
                  </label>
                </div>
              </div>
              <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Citazione</Label><Input data-testid="settings-login-quote" value={appForm.login_quote} onChange={(e) => setAppForm({ ...appForm, login_quote: e.target.value })} className="h-10 rounded-lg" /></div>
              {previewLogin && appForm.login_image_url && (
                <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 h-40 relative">
                  <img src={appForm.login_image_url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  <div className="absolute bottom-3 left-3"><p className="text-white font-outfit font-bold text-sm">"{appForm.login_quote}"</p></div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPreviewLogin(!previewLogin)} className="rounded-lg text-xs"><Eye className="w-3 h-3 mr-1" />{previewLogin ? "Nascondi" : "Anteprima"}</Button>
                <Button onClick={saveAppSettings} disabled={saving} style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg text-sm"><Save className="w-3.5 h-3.5 mr-1" />{saving ? "..." : "Salva"}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile */}
        <TabsContent value="profile" className="mt-4 space-y-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader><CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Profilo</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center shrink-0">
                  {profileForm.avatar_url ? <img src={profileForm.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-xl font-bold text-white">{profileForm.name?.charAt(0)?.toUpperCase() || "U"}</span>}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900 dark:text-slate-100">{profileForm.name || "Utente"}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
                </div>
                <label className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors text-sm text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600" data-testid="upload-avatar-btn">
                  <Upload className="w-4 h-4" />
                  {uploading === "avatar" ? "..." : "Foto"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], "avatar")} />
                </label>
              </div>
              <div className="space-y-2"><Label className="text-slate-700 dark:text-slate-200">Nome</Label><Input data-testid="settings-profile-name" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className="h-10 rounded-lg" /></div>
              <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg"><Save className="w-4 h-4 mr-2" />{saving ? "..." : "Salva"}</Button>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader><CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100 flex items-center gap-2"><Lock className="w-4 h-4" /> Cambia Password</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200">Password Attuale</Label>
                <Input data-testid="current-password-input" type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} className="h-10 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-200">Nuova Password</Label>
                  <Input data-testid="new-password-input" type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} className="h-10 rounded-lg" placeholder="Min. 6 caratteri" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-200">Conferma Password</Label>
                  <Input data-testid="confirm-password-input" type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} className="h-10 rounded-lg" />
                </div>
              </div>
              <Button onClick={changePassword} disabled={saving || !passwordForm.current_password || !passwordForm.new_password} data-testid="change-password-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg">
                <Lock className="w-4 h-4 mr-2" />{saving ? "..." : "Cambia Password"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contracts */}
        <TabsContent value="contracts" className="mt-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Numerazione Contratti</CardTitle>
              <CardDescription>Gestisci la numerazione progressiva dei contratti di noleggio</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-1">
                <p className="text-sm text-slate-700 dark:text-slate-300">Prossimo contratto: <span className="font-outfit font-bold text-lg text-slate-900 dark:text-slate-100">{contractCounter.sequence + 1}/{contractCounter.year}</span></p>
                <p className="text-xs text-slate-400">La numerazione si resetta automaticamente ogni anno</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-200">Ultimo Numero Emesso</Label>
                  <Input data-testid="contract-sequence-input" type="number" min="0" value={contractCounter.sequence} onChange={(e) => setContractCounter({ ...contractCounter, sequence: parseInt(e.target.value) || 0 })} className="h-10 rounded-lg" />
                  <p className="text-[11px] text-slate-400">Imposta a 0 per ricominciare da 1</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 dark:text-slate-200">Anno</Label>
                  <Input data-testid="contract-year-input" type="number" value={contractCounter.year} onChange={(e) => setContractCounter({ ...contractCounter, year: parseInt(e.target.value) || new Date().getFullYear() })} className="h-10 rounded-lg" />
                </div>
              </div>
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-100 dark:border-amber-800">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">Attenzione: modificare la numerazione potrebbe creare duplicati se esistono già contratti con lo stesso numero.</p>
              </div>
              <Button onClick={saveContractCounter} disabled={saving} data-testid="save-contract-counter-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg">
                <Save className="w-4 h-4 mr-2" />{saving ? "Salvataggio..." : "Salva Numerazione"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Firma */}
        <TabsContent value="signature" className="mt-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Posizione e Dimensione Firme</CardTitle>
              <CardDescription>Trascina le firme sull'anteprima del contratto per posizionarle e regola la dimensione</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Scale slider */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700 dark:text-slate-200">Scala Firma</Label>
                  <span className="text-sm font-mono font-bold text-slate-900 dark:text-slate-100" data-testid="signature-scale-value">{signatureScale.toFixed(1)}x</span>
                </div>
                <input type="range" min="0.5" max="3.0" step="0.1" value={signatureScale} onChange={(e) => setSignatureScale(parseFloat(e.target.value))} className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-violet-600" data-testid="signature-scale-slider" />
                <div className="flex justify-between text-xs text-slate-400">
                  <span>0.5x</span><span>1.0x</span><span>2.0x</span><span>3.0x</span>
                </div>
              </div>

              {/* Contract Preview with draggable signatures */}
              <div className="space-y-2">
                {!contractPreview ? (
                  <Button onClick={loadContractPreview} disabled={previewLoading} variant="outline" className="w-full rounded-lg" data-testid="load-preview-btn">
                    <Eye className="w-4 h-4 mr-2" />{previewLoading ? "Caricamento..." : "Carica Anteprima Contratto"}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Trascina le firme nella posizione desiderata:</p>
                    <div
                      ref={previewRef}
                      className="relative border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden select-none"
                      style={{ cursor: dragging !== null ? "grabbing" : "default" }}
                      data-testid="signature-preview-canvas"
                    >
                      <img src={`data:image/png;base64,${contractPreview}`} alt="Anteprima contratto" className="w-full h-auto pointer-events-none" draggable={false} />
                      {/* Draggable signature overlays */}
                      {signaturePositions.map((pos, idx) => {
                        const colors = ["violet", "blue", "emerald"];
                        const c = colors[idx];
                        const imgW = previewRef.current?.offsetWidth || 600;
                        const scaleRatio = imgW / pdfDims.w;
                        const sigW = 190 * signatureScale * scaleRatio;
                        const sigH = 35 * signatureScale * scaleRatio;
                        const left = pos.x * scaleRatio - sigW / 2;
                        const top = pos.y * scaleRatio - sigH / 2;
                        return (
                          <div
                            key={idx}
                            onMouseDown={(e) => handleMouseDown(idx, e)}
                            className={`absolute flex items-center justify-center border-2 rounded cursor-grab active:cursor-grabbing transition-shadow ${dragging === idx ? "shadow-lg z-20" : "z-10"}`}
                            style={{
                              left: `${left}px`, top: `${top}px`, width: `${sigW}px`, height: `${sigH}px`,
                              borderColor: `var(--tw-${c})`,
                              backgroundColor: idx === 0 ? "rgba(139,92,246,0.15)" : idx === 1 ? "rgba(59,130,246,0.15)" : "rgba(16,185,129,0.15)",
                              borderStyle: "dashed",
                            }}
                            data-testid={`draggable-sig-${idx}`}
                          >
                            <span className="text-[9px] font-bold select-none pointer-events-none" style={{ color: idx === 0 ? "#8b5cf6" : idx === 1 ? "#3b82f6" : "#10b981" }}>
                              {pos.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Position coordinates display */}
                    <div className="grid grid-cols-3 gap-2">
                      {signaturePositions.map((pos, idx) => {
                        const colors = [
                          "text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800",
                          "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800",
                          "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800"
                        ];
                        return (
                          <div key={idx} className={`p-2 rounded-lg border text-center ${colors[idx]}`}>
                            <p className="text-[10px] font-medium">{pos.label}</p>
                            <p className="text-[10px] font-mono mt-0.5">x:{pos.x} y:{pos.y}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-100 dark:border-blue-800">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-400">La stessa firma viene applicata 3 volte nel contratto. Trascina i riquadri per posizionare ciascuna istanza.</p>
              </div>

              {/* Save tablet signature toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Registra firma da tablet</p>
                  <p className="text-xs text-slate-400 mt-0.5">Salva la firma fatta dal tablet anche nell'anagrafica cliente</p>
                </div>
                <button
                  onClick={() => setSaveTabletSignature(!saveTabletSignature)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${saveTabletSignature ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
                  data-testid="save-tablet-sig-toggle"
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${saveTabletSignature ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
              </div>

              <Button onClick={saveSignatureScale} disabled={saving} data-testid="save-signature-scale-button" style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg">
                <Save className="w-4 h-4 mr-2" />{saving ? "..." : "Salva Posizione e Dimensione"}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl mt-4">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Dispositivi Kiosk e Tablet</CardTitle>
              <CardDescription>Link da aprire sui dispositivi: salvali nei preferiti del tablet/kiosk</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: "Firma su Tablet", desc: "Pagina dove il cliente firma quando invii una richiesta dal gestionale", path: "/firma-kiosk", icon: Tablet },
                { label: "Kiosk Self-Service Clienti", desc: "Postazione self-service: ritiro, riconsegna e firma in autonomia", path: "/kiosk-clienti", icon: Monitor },
              ].map((d) => {
                const url = `${window.location.origin}${d.path}`;
                const Icon = d.icon;
                return (
                  <div key={d.path} className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className="w-5 h-5 text-slate-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{d.label}</p>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">{d.desc}</p>
                        <p className="text-xs font-mono text-slate-500 dark:text-slate-400 truncate" data-testid={`device-url${d.path}`}>{url}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button variant="outline" size="sm" className="h-8 rounded-lg" data-testid={`copy-link${d.path}`}
                        onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copiato"); }}>
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copia
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 rounded-lg" data-testid={`open-link${d.path}`}
                        onClick={() => window.open(url, "_blank")}>
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Apri
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email */}
        <TabsContent value="email" className="mt-4 space-y-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Configurazione Email</CardTitle>
              <CardDescription>Scegli il provider e configura le credenziali</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200">Provider Email</Label>
                <Select value={emailForm.provider} onValueChange={(v) => setEmailForm({ ...emailForm, provider: v, config: {} })}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="email-provider-select"><SelectValue placeholder="Seleziona provider" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="smtp">SMTP (Gmail, Outlook, etc.)</SelectItem>
                    <SelectItem value="resend">Resend</SelectItem>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {emailForm.provider === "smtp" && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="space-y-1"><Label className="text-xs">Host SMTP</Label><Input value={emailForm.config.host || ""} onChange={(e) => updateConfig("host", e.target.value)} placeholder="smtp.gmail.com" className="h-9 rounded-lg text-sm" data-testid="smtp-host" /></div>
                  <div className="space-y-1"><Label className="text-xs">Porta</Label><Input value={emailForm.config.port || ""} onChange={(e) => updateConfig("port", e.target.value)} placeholder="587" className="h-9 rounded-lg text-sm" data-testid="smtp-port" /></div>
                  <div className="space-y-1"><Label className="text-xs">Username/Email</Label><Input value={emailForm.config.username || ""} onChange={(e) => updateConfig("username", e.target.value)} placeholder="tuoemail@gmail.com" className="h-9 rounded-lg text-sm" data-testid="smtp-username" /></div>
                  <div className="space-y-1"><Label className="text-xs">Password/App Password</Label><Input type="password" value={emailForm.config.password || ""} onChange={(e) => updateConfig("password", e.target.value)} className="h-9 rounded-lg text-sm" data-testid="smtp-password" /></div>
                  <div className="space-y-1 col-span-2"><Label className="text-xs">Email Mittente</Label><Input value={emailForm.config.from_email || ""} onChange={(e) => updateConfig("from_email", e.target.value)} placeholder="noreply@tuodominio.it" className="h-9 rounded-lg text-sm" data-testid="smtp-from" /></div>
                </div>
              )}
              {emailForm.provider === "resend" && (
                <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="space-y-1"><Label className="text-xs">API Key</Label><Input type="password" value={emailForm.config.api_key || ""} onChange={(e) => updateConfig("api_key", e.target.value)} placeholder="re_xxxxxxxxx" className="h-9 rounded-lg text-sm" data-testid="resend-key" /></div>
                  <div className="space-y-1"><Label className="text-xs">Email Mittente</Label><Input value={emailForm.config.from_email || ""} onChange={(e) => updateConfig("from_email", e.target.value)} placeholder="noreply@tuodominio.it" className="h-9 rounded-lg text-sm" data-testid="resend-from" /></div>
                </div>
              )}
              {emailForm.provider === "sendgrid" && (
                <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="space-y-1"><Label className="text-xs">API Key</Label><Input type="password" value={emailForm.config.api_key || ""} onChange={(e) => updateConfig("api_key", e.target.value)} placeholder="SG.xxxxxxxxx" className="h-9 rounded-lg text-sm" data-testid="sendgrid-key" /></div>
                  <div className="space-y-1"><Label className="text-xs">Email Mittente</Label><Input value={emailForm.config.from_email || ""} onChange={(e) => updateConfig("from_email", e.target.value)} placeholder="noreply@tuodominio.it" className="h-9 rounded-lg text-sm" data-testid="sendgrid-from" /></div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Button onClick={saveEmailSettings} disabled={saving} style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg text-sm" data-testid="save-email-settings"><Save className="w-3.5 h-3.5 mr-1" />{saving ? "..." : "Salva Config"}</Button>
                {emailForm.provider && (
                  <div className="flex items-center gap-2 ml-2">
                    <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Email test" className="h-9 w-48 rounded-lg text-sm" data-testid="test-email-input" />
                    <Button variant="outline" size="sm" onClick={sendTestEmail} disabled={testingEmail} className="rounded-lg text-xs" data-testid="send-test-email">
                      <Send className="w-3 h-3 mr-1" />{testingEmail ? "..." : "Test"}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Template Email</CardTitle>
              <CardDescription>Personalizza i modelli delle email inviate ai clienti</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2 p-2.5 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-100 dark:border-blue-800">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-400">Segnaposto disponibili: <span className="font-mono text-[10px]">{placeholders}</span></p>
              </div>
              {Object.entries(templateLabels).map(([key, label]) => (
                <div key={key} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <button onClick={() => setEditingTemplate(editingTemplate === key ? null : key)} className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" data-testid={`template-toggle-${key}`}>
                    <span className="font-medium text-sm text-slate-700 dark:text-slate-300">{label}</span>
                    <span className="text-xs text-slate-400">{editingTemplate === key ? "Chiudi" : "Modifica"}</span>
                  </button>
                  {editingTemplate === key && (
                    <div className="p-3 space-y-3 bg-white dark:bg-slate-900">
                      <div className="space-y-1">
                        <Label className="text-xs">Oggetto</Label>
                        <Input value={emailForm.templates[key]?.subject || ""} onChange={(e) => updateTemplate(key, "subject", e.target.value)} className="h-9 rounded-lg text-sm" data-testid={`template-subject-${key}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Corpo (HTML)</Label>
                        <textarea value={emailForm.templates[key]?.body || ""} onChange={(e) => updateTemplate(key, "body", e.target.value)} className="w-full min-h-[120px] p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono bg-white dark:bg-slate-800 dark:text-slate-200 resize-y focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" data-testid={`template-body-${key}`} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <Button onClick={saveEmailSettings} disabled={saving} style={{ backgroundColor: accent }} className="hover:opacity-90 text-white rounded-lg text-sm" data-testid="save-templates-button">
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? "..." : "Salva Template"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sistema */}
        <TabsContent value="system" className="mt-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Aggiornamento App</CardTitle>
              <CardDescription>Aggiorna l'applicazione all'ultima versione da GitHub</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Updater status */}
              <div className={`flex items-center gap-3 p-3 rounded-lg border ${updaterOnline ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"}`} data-testid="updater-status">
                {updaterOnline ? <Wifi className="w-4 h-4 text-emerald-600" /> : <WifiOff className="w-4 h-4 text-slate-400" />}
                <div>
                  <p className={`text-sm font-medium ${updaterOnline ? "text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"}`}>
                    {updaterOnline ? "Servizio Updater attivo" : "Servizio Updater non disponibile"}
                  </p>
                  {!updaterOnline && <p className="text-xs text-slate-400 mt-0.5">Installa il servizio updater sul server per abilitare gli aggiornamenti da remoto</p>}
                </div>
              </div>

              {/* Last update status */}
              {updateStatus && updateStatus.status !== "idle" && updateStatus.status !== "offline" && (
                <div className={`flex items-center gap-3 p-3 rounded-lg border ${
                  updateStatus.status === "success" ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800" :
                  updateStatus.status === "error" ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800" :
                  "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                }`} data-testid="update-last-status">
                  {updateStatus.status === "success" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
                   updateStatus.status === "error" ? <XCircle className="w-4 h-4 text-red-600" /> :
                   <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />}
                  <div>
                    <p className={`text-sm font-medium ${
                      updateStatus.status === "success" ? "text-emerald-700 dark:text-emerald-400" :
                      updateStatus.status === "error" ? "text-red-700 dark:text-red-400" :
                      "text-blue-700 dark:text-blue-400"
                    }`}>{updateStatus.message}</p>
                    {updateStatus.timestamp && <p className="text-xs text-slate-400 mt-0.5">{new Date(updateStatus.timestamp).toLocaleString("it-IT")}</p>}
                  </div>
                </div>
              )}

              {/* Update button */}
              <Button
                onClick={triggerUpdate}
                disabled={updating || !updaterOnline}
                data-testid="trigger-update-button"
                style={{ backgroundColor: updaterOnline ? accent : undefined }}
                className={`${updaterOnline ? "hover:opacity-90 text-white" : ""} rounded-lg`}
                variant={updaterOnline ? "default" : "outline"}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${updating ? "animate-spin" : ""}`} />
                {updating ? "Aggiornamento in corso..." : "Aggiorna App"}
              </Button>

              {/* Setup instructions when offline */}
              {updaterOnline === false && (
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Installazione (una sola volta):</p>
                  <div className="bg-slate-900 dark:bg-slate-950 rounded-lg p-3 font-mono text-xs text-slate-300 space-y-1 select-all">
                    <p>cd ~/rgrent/docker/updater && bash install.sh</p>
                  </div>
                  <p className="text-xs text-slate-400">Dopo l'installazione, ricarica questa pagina. Il pulsante "Aggiorna App" funzionera automaticamente.</p>
                </div>
              )}

              {/* Refresh status button */}
              <Button variant="outline" size="sm" onClick={checkUpdateStatus} className="rounded-lg text-xs" data-testid="refresh-status-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Aggiorna stato
              </Button>
            </CardContent>
          </Card>

          {/* Kiosk PIN */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl mt-4">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Sicurezza Kiosk</CardTitle>
              <CardDescription>PIN di accesso per la pagina firma tablet</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm text-slate-700 dark:text-slate-300">PIN Kiosk (4-6 cifre)</Label>
                <div className="flex gap-3 items-center">
                  <Input
                    data-testid="kiosk-pin-setting"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={kioskPin}
                    onChange={(e) => setKioskPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="Nessun PIN"
                    className="max-w-[200px] text-center tracking-[0.3em] rounded-lg"
                  />
                  <Button
                    data-testid="save-kiosk-pin-btn"
                    onClick={async () => {
                      try {
                        const api = axiosAuth();
                        await api.put("/settings", { kiosk_pin: kioskPin });
                        toast.success(kioskPin ? "PIN Kiosk salvato" : "PIN Kiosk rimosso");
                      } catch { toast.error("Errore salvataggio PIN"); }
                    }}
                    size="sm"
                    style={{ backgroundColor: accent }}
                    className="hover:opacity-90 text-white rounded-lg"
                  >
                    Salva
                  </Button>
                </div>
                <p className="text-xs text-slate-400">{kioskPin ? `PIN attivo: ${kioskPin.length} cifre` : "Nessun PIN — il kiosk e' accessibile senza autenticazione"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Kiosk Foto Identificativa */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl mt-4">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Foto Identificativa Kiosk</CardTitle>
              <CardDescription>Scatto foto webcam durante il ritiro veicolo</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-700 dark:text-slate-300">Richiedi foto al ritiro</p>
                  <p className="text-xs text-slate-400 mt-0.5">Il cliente dovra' scattare una foto identificativa prima di firmare</p>
                </div>
                <button
                  data-testid="kiosk-photo-toggle"
                  onClick={async () => {
                    try {
                      const api = axiosAuth();
                      const newVal = !(settings.kiosk_require_photo ?? true);
                      await api.put("/settings", { kiosk_require_photo: newVal });
                      updateSettings({ ...settings, kiosk_require_photo: newVal });
                      toast.success(newVal ? "Foto identificativa attivata" : "Foto identificativa disattivata");
                    } catch { toast.error("Errore salvataggio"); }
                  }}
                  className={`relative w-11 h-6 rounded-full transition-colors ${(settings.kiosk_require_photo ?? true) ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${(settings.kiosk_require_photo ?? true) ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab Utenti ── */}
        <TabsContent value="users" className="mt-4 space-y-4">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="font-outfit text-base text-slate-900 dark:text-slate-100">Gestione Utenti</CardTitle>
              <CardDescription>Utenti registrati nel sistema</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`user-row-${u.id}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm" style={{ backgroundColor: accent }}>
                      {(u.name || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{u.name || "Senza nome"}</p>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {resetPwUserId === u.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="text"
                            placeholder="Nuova password"
                            value={resetPwValue}
                            onChange={(e) => setResetPwValue(e.target.value)}
                            className="h-8 w-36 text-xs rounded-lg"
                          />
                          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                            try {
                              const api = axiosAuth();
                              await api.post(`/auth/users/${u.id}/reset-password`, { new_password: resetPwValue });
                              toast.success(`Password reimpostata per ${u.email}`);
                              setResetPwUserId(null); setResetPwValue("");
                            } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
                          }}>Salva</Button>
                          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setResetPwUserId(null); setResetPwValue(""); }}>Annulla</Button>
                        </div>
                      ) : (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                            title="Reset Password" onClick={() => { setResetPwUserId(u.id); setResetPwValue(""); }}
                            data-testid={`reset-pw-${u.id}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          {u.id !== user?.id && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                              title="Elimina Utente"
                              onClick={async () => {
                                if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
                                try {
                                  const api = axiosAuth();
                                  await api.delete(`/auth/users/${u.id}`);
                                  toast.success("Utente eliminato");
                                  fetchUsers();
                                } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
                              }}
                              data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <div className="text-center py-8 text-sm text-slate-400">Nessun utente trovato</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">URL Applicazione</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Necessario per i link di reset password via email</p>
              <div className="flex gap-2">
                <Input
                  placeholder="https://rgrentcar.com"
                  value={settings.app_url || ""}
                  onChange={(e) => updateSettings({ ...settings, app_url: e.target.value })}
                  className="flex-1 h-9 text-sm rounded-lg"
                  data-testid="app-url-input"
                />
                <Button size="sm" className="h-9" style={{ backgroundColor: accent }}
                  onClick={async () => {
                    try {
                      const api = axiosAuth();
                      await api.put("/settings", { app_url: settings.app_url });
                      toast.success("URL salvato");
                    } catch { toast.error("Errore"); }
                  }}>
                  <Save className="w-3.5 h-3.5 mr-1 text-white" /> <span className="text-white">Salva</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
